<script setup lang="ts">
import { onMounted, ref } from "vue";

import { useFocusTrap } from "@/composables/useFocusTrap";

/**
 * Full-screen session overlay: 74% white scrim + 10px backdrop blur
 * (PRD PE-6.8 / §6.8). A session is a *verb*, never a destination, so it has no
 * route and renders over whatever the student was looking at — except on a
 * phone, where AppShell places it in the page below the nav (GitHub #378).
 *
 * Escape emits `requestClose` rather than closing directly. Once a focus block
 * is running, discarding it silently would throw away the block a student just
 * earned — the caller decides whether to confirm first.
 */

defineProps<{ label: string }>();

const emit = defineEmits<{ requestClose: [] }>();

const shell = ref<HTMLElement | null>(null);
useFocusTrap(shell);

// On a phone AppShell lays the overlay out in the page under the nav instead of
// fixing it over the viewport (GitHub #378). The page it replaces may have been
// scrolled, so start from the top: nav first, then the session.
onMounted(() => {
  if (shell.value && getComputedStyle(shell.value).position !== "fixed") {
    window.scrollTo({ top: 0 });
  }
});
</script>

<template>
  <div
    ref="shell"
    class="overlay rl-anim-overlay-in"
    role="dialog"
    aria-modal="true"
    :aria-label="label"
    @keydown.escape="emit('requestClose')"
  >
    <button
      type="button"
      class="overlay-close"
      aria-label="Close"
      @click="emit('requestClose')"
    >
      &times;
    </button>
    <div class="overlay-body rl-anim-fade-up">
      <slot />
    </div>
  </div>
</template>

<style scoped>
.overlay {
  position: fixed;
  inset: 0;
  z-index: 60;
  overflow-y: auto;
  background: rgba(255, 255, 255, 0.74);
  backdrop-filter: blur(10px);
}

.overlay:focus-visible {
  outline: none;
}

.overlay-close {
  position: absolute;
  top: var(--sp-4);
  right: var(--sp-4);
  width: 32px;
  height: 32px;
  font-size: 22px;
  line-height: 1;
  color: var(--rl-fg-3);
  cursor: pointer;
  background: none;
  border: none;
  border-radius: var(--radius-sm);
}

.overlay-close:hover {
  color: var(--rl-fg-1);
  background: var(--rl-bg-muted);
}

/* The top padding clears the close button (its offset, height and a gap), so
   a full-width card never runs under it. Content that sizes itself to the
   visible height reads it from the custom property.

   The entrance animation keeps this body a later stacking layer than the
   close button, so where the body spans the corner its empty padding would
   swallow the tap. Only the content takes pointer events; a confirm scrim
   inside it still covers the button, as it should. */
.overlay-body {
  --overlay-body-pad-top: calc(var(--sp-4) + 32px + var(--sp-2));
  max-width: 1000px;
  margin: 0 auto;
  padding: var(--overlay-body-pad-top) var(--sp-6) var(--sp-8);
  pointer-events: none;
}

.overlay-body > :deep(*) {
  pointer-events: auto;
}
</style>
